// @ts-check

/** @file Reads Expo Router params with fail-loud Typanic validators. */

import {forcedNonBlankStringParam, forcedPositiveIntegerParam, optionalPositiveIntegerList, optionalStringParam} from "typanic"

/**
 * @typedef {Record<string, string | string[] | undefined>} RouteParams
 */

/**
 * Reads a required single route param.
 * @param {RouteParams} params Route params from Expo Router.
 * @param {string} key Param key to read.
 * @param {string} [label] Human-readable param label for errors.
 * @returns {string} Single route param value.
 * @throws {Error} When the param is missing, blank, or repeated.
 */
export function requiredRouteParam(params, key, label = key) {
  return forcedNonBlankStringParam(params, key, label)
}

/**
 * Reads a required positive integer route param.
 * @param {RouteParams} params Route params from Expo Router.
 * @param {string} key Param key to read.
 * @param {string} [label] Human-readable param label for errors.
 * @returns {number} Parsed positive integer identifier.
 * @throws {Error} When the param is missing, blank, repeated, or malformed.
 */
export function requiredPositiveIntegerRouteParam(params, key, label = key) {
  return forcedPositiveIntegerParam(params, key, label)
}

/**
 * Reads an optional single route param.
 * @param {RouteParams} params Route params from Expo Router.
 * @param {string} key Param key to read.
 * @param {string} [label] Human-readable param label for errors.
 * @returns {string | null} Single route param value, or null when absent.
 * @throws {Error} When the param is repeated.
 */
export function optionalRouteParam(params, key, label = key) {
  return optionalStringParam(params, key, label)
}

/**
 * Parses route or select values into sorted positive integer identifiers.
 * @param {(string | number)[] | string | number | null | undefined} value Route or select value.
 * @param {string} [label] Human-readable value label for errors.
 * @returns {number[]} Sorted positive integer identifiers.
 */
export function positiveIntegerListParam(value, label = "value") {
  const ids = optionalPositiveIntegerList(value, label) ?? []
  ids.sort((a, b) => a - b)

  return ids
}

/**
 * Normalizes a route parameter containing one or more canonical database IDs.
 * @param {unknown} value Route parameter value.
 * @param {string} [label] Human-readable parameter name.
 * @returns {string[]} Sorted, unique non-blank identifiers.
 */
export function identifierListParam(value, label = "value") {
  const candidates = Array.isArray(value) ? value : value == null ? [] : [value]
  const ids = candidates.map((candidate) => {
    if (typeof candidate !== "string" || candidate.trim() === "") {
      throw new TypeError(`${label} must contain non-blank string identifiers`)
    }
    return candidate.trim()
  })
  return [...new Set(ids)].sort()
}
